/**
 * 활동 로그 테이블 컴포넌트
 * activity-logs/admin/index.tsx에서 분리
 */
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDateAndTimeToKST } from '@/utils/dateUtils';
import { getActivityTypeLabel } from '@/utils/activityLogUtils';

interface ActivityLog {
  id: number;
  created_at: string;
  activity_type: string;
  description?: string | null;
  ip_address?: string | null;
  log_level?: string | null;
  user?: {
    name?: string | null;
    email: string;
    is_deleted?: boolean;
  } | null;
  customer?: {
    id: number;
    customer_code?: string;
    customer_name?: string | null;
    alias?: string | null;
    is_deleted?: boolean;
  } | null;
}

interface ActivityLogTableProps {
  logs: ActivityLog[];
  showCustomerColumn?: boolean;
  isAllLogsView?: boolean;
}

export const ActivityLogTable: React.FC<ActivityLogTableProps> = ({
  logs,
  showCustomerColumn = false,
  isAllLogsView = false,
}) => {
  const getLogLevelBgColor = (logLevel?: string | null) => {
    if (!isAllLogsView || !logLevel) return '';
    switch (logLevel.toUpperCase()) {
      case 'WARN':
        return 'bg-yellow-50 hover:bg-yellow-100';
      case 'ERROR':
        return 'bg-red-50 hover:bg-red-100';
      default:
        return '';
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>시간</TableHead>
            {showCustomerColumn && <TableHead>고객사 ID</TableHead>}
            <TableHead>사용자</TableHead>
            <TableHead>활동 유형</TableHead>
            <TableHead>설명</TableHead>
            <TableHead>IP 주소</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showCustomerColumn ? 6 : 5}>
                <EmptyState message="로그가 없습니다." />
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => {
              const { dateStr, timeStr } = formatDateAndTimeToKST(log.created_at);
              return (
                <TableRow key={log.id} className={getLogLevelBgColor(log.log_level)}>
                  <TableCell className="font-mono text-xs">
                    <div className="leading-tight">
                      <div>{dateStr}</div>
                      <div className="text-gray-500">{timeStr}</div>
                    </div>
                  </TableCell>
                  {showCustomerColumn && (
                    <TableCell>
                      {log.customer ? (
                        <div className="leading-tight">
                          <div className="font-medium">
                            {log.customer.customer_code || log.customer.id}
                          </div>
                          {log.customer.alias && (
                            <div className="text-xs text-gray-500">{log.customer.alias}</div>
                          )}
                          {log.customer.is_deleted && (
                            <div className="text-xs text-red-500">(삭제됨)</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {log.user ? (
                      <div>
                        <div className="font-medium">
                          {log.user.name || log.user.email}
                          {log.user.is_deleted && (
                            <span className="ml-1 text-xs text-gray-500">(삭제됨)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{log.user.email}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="info" size="sm">
                      {getActivityTypeLabel(log.activity_type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="line-clamp-2 text-sm" title={log.description || ''}>
                      {log.description || '-'}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{log.ip_address || '-'}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ActivityLogTable;
